import { useEffect, useState } from 'react';
import type { LmsFilters, LmsResult, LmsSupplierFacet } from '@/interfaces/lms';
import { updateListFilter } from '@/lib/utils/lmsQueryParams';
import displayValues from '@/resources/displayValues.json';
import { type CheckboxOption, SimpleCheckboxFilter } from './checkbox/SimpleCheckboxFilter';

type LmsOriginFilterProps = {
    data?: LmsResult['supplierFacet'];
    filters: LmsFilters;
    updateFilters: (key: string, value: string[]) => void;
};

const extractSupplierValues = (facets: LmsSupplierFacet[] | undefined): string[] => facets?.map((facet) => facet.name) ?? [];

export const LmsSupplierFilter = ({ data, filters, updateFilters }: LmsOriginFilterProps) => {
    const [charter, setCharter] = useState<string[]>(extractSupplierValues(data?.charter));
    const [nonCharter, setNonCharter] = useState<string[]>(extractSupplierValues(data?.nonCharter));

    /**
     * To improve the rendering of suppliers that may be missing in API data responses and/or are disabled all together,
     * these `useState`s and `useEffect` will keep all suppliers that have been received from the API at any point, so that
     * they do not disappear from the filter list whenever we filter away all offers for a supplier.
     *
     * This should probably be improved from the backend (i.e. the response contains all possible supplier values).
     */
    useEffect(() => {
        setCharter(Array.from(new Set(charter.concat(extractSupplierValues(data?.charter)))));
        setNonCharter(Array.from(new Set(nonCharter.concat(extractSupplierValues(data?.nonCharter)))));
    }, [data]);

    const onChange = (value: string): void => {
        updateFilters('med', updateListFilter(value, filters.med));
    };

    const mapOptions = (suppliers: string[], facets?: LmsSupplierFacet[]): CheckboxOption[] =>
        suppliers
            .map((supplier) => ({
                value: supplier,
                text: displayValues.suppliers[supplier],
                count: facets?.find((facet) => facet.name === supplier)?.count ?? 0,
            }))
            .sort((a, b) => b.count - a.count);

    const charterOptions = mapOptions(charter, data?.charter);
    const nonCharterOptions = mapOptions(nonCharter, data?.nonCharter);

    return (
        <>
            <SimpleCheckboxFilter
                id="charterSuppliers"
                title="Charter"
                options={charterOptions}
                checked={filters.med}
                onChange={onChange}
            />
            <SimpleCheckboxFilter
                id="nonCharterSuppliers"
                title="Fly + hotell"
                options={nonCharterOptions}
                checked={filters.med}
                onChange={onChange}
            />
        </>
    );
};
